import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';

const WINDOW_TOKEN = new InjectionToken<Window>('A reference to the window object', {
  factory: () => window,
});

/* Window object encapsulation service */
@Injectable({
  providedIn: 'root',
})
export class WindowService {
  private readonly isBrowser: boolean;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW_TOKEN);
  private readonly memoryFallback = new Map<string, string>();
  private readonly sessionMemoryFallback = new Map<string, string>();
  private readonly safeLocalStorage: Storage | null;
  private readonly safeSessionStorage: Storage | null;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.safeLocalStorage = this.isBrowser ? this.getUsableLocalStorage(this.window) : null;
    this.safeSessionStorage = this.isBrowser ? this.getUsableSessionStorage(this.window) : null;
  }

  private getUsableLocalStorage(windowRef: Window | null | undefined): Storage | null {
    if (!windowRef) return null;
    try {
      const storage = windowRef.localStorage;
      if (!storage) return null;

      // Test if writing to storage actually works
      const testKey = '__storage_test__' + Math.random().toString(36).slice(2);
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch {
      console.warn('LocalStorage is not available, memory fallback will be used.');
      return null;
    }
  }

  private getUsableSessionStorage(windowRef: Window | null | undefined): Storage | null {
    if (!windowRef) return null;
    try {
      const storage = windowRef.sessionStorage;
      if (!storage) return null;

      const testKey = '__session_storage_test__' + Math.random().toString(36).slice(2);
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch {
      console.warn('SessionStorage is not available, memory fallback will be used.');
      return null;
    }
  }

  setStorage(key: string, value: string): void {
    if (!this.isBrowser) return;

    try {
      this.safeLocalStorage?.setItem(key, value);
    } catch {
      // Swallow errors caused by storage being full or blocked
    }
    this.memoryFallback.set(key, value); // Always store in memory as fallback
  }

  getStorage(key: string): string | null {
    if (!this.isBrowser) return null;

    try {
      const storedValue = this.safeLocalStorage?.getItem(key);
      if (storedValue !== null) return storedValue;
    } catch {
      // Safari may throw even on getItem when blocked
    }
    return this.memoryFallback.get(key) ?? null;
  }

  removeStorage(key: string): void {
    if (!this.isBrowser) return;

    try {
      this.safeLocalStorage?.removeItem(key);
    } catch {
      // Ignore errors in case storage is unavailable
    }

    this.memoryFallback.delete(key);
  }

  clearStorage(): void {
    if (!this.isBrowser) return;

    try {
      this.safeLocalStorage?.clear();
    } catch {
      // Ignore potential exceptions (e.g., Safari private mode or storage blocked)
    }

    this.memoryFallback.clear();
  }

  setSessionStorage(key: string, value: string): void {
    if (!this.isBrowser) return;

    try {
      this.safeSessionStorage?.setItem(key, value);
    } catch {
      // Swallow errors caused by storage being full or blocked
    }
    this.sessionMemoryFallback.set(key, value);
  }

  getSessionStorage(key: string): string | null {
    if (!this.isBrowser) return null;

    try {
      const storedValue = this.safeSessionStorage?.getItem(key);
      if (storedValue !== null) return storedValue;
    } catch {
      // Safari may throw even on getItem when blocked
    }
    return this.sessionMemoryFallback.get(key) ?? null;
  }

  removeSessionStorage(key: string): void {
    if (!this.isBrowser) return;

    try {
      this.safeSessionStorage?.removeItem(key);
    } catch {
      // Ignore errors in case storage is unavailable
    }

    this.sessionMemoryFallback.delete(key);
  }
}
